import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type LedgerType = '' | 'BOOKING_POOL' | 'INSTALLMENT_POOL';

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export interface LedgerFilters {
  month: string;
  type: LedgerType;
  page: number;
  /** True when a month or type filter narrows the ledger. */
  active: boolean;
  setMonth: (month: string) => void;
  setType: (type: LedgerType) => void;
  setPage: (page: number) => void;
  clear: () => void;
}

/** Ledger month/type/page held in the URL (`?month=2026-10&type=BOOKING_POOL&page=2`) so refresh and back keep place. */
export function useLedgerFilters(): LedgerFilters {
  const [params, setParams] = useSearchParams();
  const rawMonth = params.get('month') ?? '';
  const rawType = params.get('type');
  const rawPage = Number(params.get('page'));

  const month = MONTH_RE.test(rawMonth) ? rawMonth : '';
  const type: LedgerType = rawType === 'BOOKING_POOL' || rawType === 'INSTALLMENT_POOL' ? rawType : '';
  const page = Number.isInteger(rawPage) && rawPage > 1 ? rawPage : 1;

  const update = useCallback(
    (patch: Record<string, string>) => {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
          if (value && !(key === 'page' && value === '1')) next.set(key, value);
          else next.delete(key);
        }
        return next;
      });
    },
    [setParams],
  );

  return useMemo(
    () => ({
      month,
      type,
      page,
      active: month !== '' || type !== '',
      setMonth: (value: string) => update({ month: value, page: '' }),
      setType: (value: LedgerType) => update({ type: value, page: '' }),
      setPage: (value: number) => update({ page: String(Math.max(1, Math.floor(value))) }),
      clear: () => update({ month: '', type: '', page: '' }),
    }),
    [month, type, page, update],
  );
}
